import { useQuery } from 'react-query';
import { archivosFetch } from '../../utils/fetch';
import { AuthUser } from '../../models/user';
import { Service } from '../../models/service';

// The lists with which names are resolved and the dropdowns are filled.
//
// Users: `GET /api/users/` is `requireAdmin` (403 for a USER, verified
// against the API). It is only requested when the user is an administrator; for the
// others the list stays empty and the "Archiveur" dropdown offers "Tous" and
// "Moi", which is the only thing the API lets them query.
//
// Services: `GET /api/services/` answers 404 with the empty list. It is not an
// error — it is read as "none yet", just as AdminEntities does.
//
// Own cache keys: `GET_USERS` and `GET_SERVICES` are used by other pages
// to store the raw `Response`, not the JSON, and sharing them would break their
// `.json()`.

export const HOME_USERS_KEY = 'HOME_USERS';
export const HOME_SERVICES_KEY = 'HOME_SERVICES';

const fetchUsers = async (): Promise<AuthUser[]> => {
  const response = await archivosFetch(
    `${process.env.REACT_APP_API_URL}/api/users/`,
  );
  if (!response.ok) return [];

  const body = await response.json();
  return Array.isArray(body?.users) ? body.users : [];
};

const fetchServices = async (): Promise<Service[]> => {
  const response = await archivosFetch(
    `${process.env.REACT_APP_API_URL}/api/services/`,
  );
  if (!response.ok) return [];

  const body = await response.json();
  return Array.isArray(body?.services) ? body.services : [];
};

export const useFilterOptions = (isAdmin: boolean) => {
  const users = useQuery<AuthUser[]>([HOME_USERS_KEY], fetchUsers, {
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const services = useQuery<Service[]>([HOME_SERVICES_KEY], fetchServices, {
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    users: users.data ?? [],
    services: services.data ?? [],
  };
};

export const fullName = (user?: Partial<AuthUser> | null): string =>
  [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim() ||
  user?.email ||
  '';
